<template>
  <div class="mx-auto max-w-content-container">
    <div class="relative">
      <div v-show="sustainabilityIndexes.length > 1" class="mb-0.5 mt-4 flex">
        <SummaryViewerTab
          v-for="sustainabilityIndex in sustainabilityIndexes"
          :key="sustainabilityIndex.name"
          class="max-w-[130px]"
          :is-active="activeMineral?.name === sustainabilityIndex.name"
          :text="sustainabilityIndex.name"
          @click="updateTabView(sustainabilityIndex.name)"
        />
      </div>
      <SwipeIndicator
        v-if="sustainabilityData[tapNameActive]?.length"
        class="absolute left-1/2 top-[100px] z-10 -translate-x-1/2 md:hidden"
      />
      <div
        class="relative h-full overflow-x-auto"
        :class="[
          isAuthorized ? 'min-h-[500px] md:min-h-[458px]' : 'min-h-[280px]',
        ]"
      >
        <div class="h-full w-full">
          <div v-if="loading" class="absolute left-0 top-20 w-full lg:top-32">
            <TheLoader />
          </div>
          <div v-else class="ag-grid-sustainability-index-page">
            <div
              v-if="hasData()"
              class="ag-grid-pa-portal table-isolation h-full"
            >
              <CustomTable
                v-if="isAuthorized"
                data-testid="sustainability-table"
                :class="[sustainabilityIndexes.length > 1 ? 'mt-0' : 'mt-4']"
                :column-defs="columnDefs"
                :row-data="sustainabilityData[tapNameActive]"
                :row-height="32"
                :default-col-def="defaultColDef"
                pagination
              />
              <div v-else class="min-w-[750px]">
                <div
                  class="absolute top-[66px] w-full min-w-[750px] border-b-4 border-p-500 md:hidden"
                />
                <UnauthorizedTableSustainabilityIndex
                  :items="sustainabilityData[tapNameActive]"
                />
              </div>
            </div>
            <div
              v-else
              class="flex h-full min-h-[280px] w-full items-center justify-center text-lg font-semibold"
            >
              Data available soon
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SwipeIndicator from '~/components/price-assessments/SwipeIndicator.vue'
import CustomTable from '~/components/price-assessments/CustomTableAG.vue'
import UnauthorizedTableSustainabilityIndex from '~/components/sustainability/UnauthorizedTableSustainabilityIndex.vue'
import CustomHeaderTable from '~/components/price-assessments/CustomHeaderTable.vue'
import SummaryViewerTab from '~/components/price-assessments/SummaryViewerTab.vue'
import { useScreenSize } from '~/composables/useScreenSize'
import { logError } from '~/utils/log-error'

/*-------------------------------------
  Props & Refs
-------------------------------------*/
const props = defineProps<{
  sustainabilityIndexes: any[]
  product: string
}>()

const emit = defineEmits(['is-authorized'])

const { isMobile } = useScreenSize()

const columnDefs = ref([])
const initialColumnsDefs = [
  {
    field: 'company',
    headerName: '',
    pinned: isMobile.value ? '' : 'left',
    cellClass: (params: any) => cellClass(params, 'border-right'),
    minWidth: 280,
  },
  {
    field: 'overall_index_performance',
    headerName: 'Classification',
    minWidth: 150,
    headerClass:
      'without-overflow bg-[#8F0000] text-white hover:!bg-[#8F0000]/[.8] text-xs !border-r !border-r-solid !border-r-white',
  },
  {
    field: 'index_score',
    headerName: 'Benchmark Sustainability Index Score',
    minWidth: 150,
    headerClass:
      'without-overflow bg-[#FDBD00] text-black hover:!bg-[#FDBD00]/[.8] text-xs !border-r !border-r-solid !border-r-white',
  },
]
const sustainabilityData = ref({})
const loading = ref(true)
const defaultColDef = {
  sortable: false,
  cellClass: (params: any) => cellClass(params, 'justify-end'),
  minWidth: 140,
  suppressMovable: true,
  resizable: false,
}
const activeMineral = ref(null)
const isReady = ref(false)
const isAuthorized = ref(false)

const tapNameActive = computed(() => activeMineral.value?.slug)

const { $restClient } = useNuxtApp()

/*-------------------------------------
  Methods
-------------------------------------*/
const hasData = () => {
  return sustainabilityData.value[tapNameActive.value]?.length > 0
}

const cellClass = (params: any, extraClass: string) => {
  const { value } = params
  if (typeof value === 'number') {
    return `!flex items-center justify-end tabular-nums !border-b !border-b-solid !border-b-white ${extraClass}`
  }
  return `!flex items-center ${extraClass}`
}

const getSustainabilityIndex = async () => {
  if (!activeMineral.value?.slug) return
  try {
    loading.value = true
    const response = await $restClient.getSustainabilityIndexByProduct(
      props.product,
      tapNameActive.value,
    )

    isAuthorized.value = response.authorized
    buildHeaders(response.headers)

    emit('is-authorized', isAuthorized.value)
    sustainabilityData.value[tapNameActive.value] = response.values.map(
      (item: any) => transformData(item),
    )
    isReady.value = hasData()
  } catch (error) {
    logError(error)
  } finally {
    loading.value = false
  }
}

const buildHeaders = (headers: any) => {
  const backgroundColors = {
    'sustainability-index-environment': 'bg-[#003A1E] hover:!bg-[#003A1E]/[.8]',
    'sustainability-index-social': 'bg-[#E25100] hover:!bg-[#E25100]/[.8]',
    'sustainability-index-governance': 'bg-[#192E5B] hover:!bg-[#192E5B]/[.8]',
  }

  const columns = headers?.map((header: any) => {
    const classes = backgroundColors[header.classes]
    return header.is_parent
      ? {
          field: header.field,
          headerName: header.header,
          headerClass: `${classes} without-overflow text-white !border-r !border-r-solid !border-r-white`,
          headerComponent: CustomHeaderTable,
          headerComponentParams: {
            childrenColumns: header.children,
          },
        }
      : {
          field: header.field,
          headerName: header.header,
          headerClass: `${classes} without-overflow text-white !text-xs !border-r !border-r-solid !border-r-white`,
          colId: header.code,
          hide: true,
        }
  })
  columnDefs.value = initialColumnsDefs.concat(columns)
}

const transformData = (originalData: any) => {
  const transformedData: any = {
    company: originalData.company,
    index_score: originalData.index_score,
    overall_index_performance: originalData.overall_index_performance,
  }

  const excludedKeys = ['company', 'index_score', 'overall_index_performance']

  const transform = (key: string, value: any) => {
    if (!excludedKeys.includes(key)) {
      transformedData[key] = value
      if (isAuthorized.value && originalData[key].children) {
        originalData[key].children.forEach((category: any) => {
          transformedData[category.label] = category.index_score
        })
      }
    }
  }

  Object.keys(originalData).forEach((key) => {
    const value = isAuthorized.value
      ? originalData[key].index_score
      : originalData[key].overall_index_performance
    transform(key, value)
  })

  return transformedData
}

const updateTabView = (typeName: string) => {
  activeMineral.value = props.sustainabilityIndexes.find(
    (sustainabilityIndex: any) => sustainabilityIndex.name === typeName,
  )
  if (
    !sustainabilityData.value[tapNameActive.value] ||
    sustainabilityData.value[tapNameActive.value].length <= 0
  ) {
    getSustainabilityIndex()
  }
}

/*-------------------------------------
  Lifecycle
-------------------------------------*/
watch(
  sustainabilityData,
  () => {
    isReady.value = hasData()
  },
  { deep: true },
)

useAuthentication(() => getSustainabilityIndex())

onMounted(async () => {
  activeMineral.value = props.sustainabilityIndexes[0]
  await getSustainabilityIndex()
})
</script>

<style lang="scss">
.table-isolation {
  .ag-body {
    border: none !important;
  }
  .ag-center-cols-container,
  .ag-pinned-left-cols-container {
    border: none !important;
    & > div {
      border: none !important;
      & > div {
        border: solid 1px #f0f1f7 !important;
      }
    }
  }
}
</style>
